import { useState, useRef, useEffect } from 'react'
import { Icon } from '@iconify/react'
import Card from '../common/Card'
import Button from '../common/Button'
import ScanPlanner from './ScanPlanner'

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async (e) => {
    e.stopPropagation()
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const el = document.createElement('textarea')
      el.value = text
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  if (!text) return null

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied!' : `Copy: ${text}`}
      className={`flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border transition-colors flex-shrink-0
        ${copied
          ? 'bg-green-50 border-green-200 text-green-600'
          : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
        }`}
    >
      <Icon icon={copied ? 'mdi:check' : 'mdi:content-copy'} className="text-sm" />
      {copied ? 'Copied' : 'Copy URL'}
    </button>
  )
}

const CustomSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  hasError = false,
  getKey,
  getValue,
  getLabel,
  getSubLabel,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const selected = options.find((o) => getValue(o) === value)

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleListScroll = (e) => {
    if (!onLoadMore || !hasMore || isLoadingMore) return
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollHeight - scrollTop - clientHeight < 40) {
      onLoadMore()
    }
  }

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border bg-white text-left transition-colors
          ${hasError ? 'border-red-400' : 'border-gray-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:border-primary cursor-pointer'}
          ${open ? 'border-primary ring-2 ring-primary/20' : ''}
        `}
      >
        <span className="flex-1 min-w-0">
          {selected ? (
            <span className="block text-sm font-medium text-gray-800 truncate">
              {getLabel(selected)}
            </span>
          ) : (
            <span className="block text-sm text-gray-400">{placeholder}</span>
          )}
          {selected && getSubLabel && (
            <span className="block text-[11px] text-gray-400 truncate">
              {getSubLabel(selected)}
            </span>
          )}
        </span>
        <Icon
          icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'}
          className="text-gray-400 text-lg shrink-0"
        />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="max-h-60 overflow-y-auto" onScroll={handleListScroll}>
            {options.map((opt) => {
              const isSelected = getValue(opt) === value
              return (
                <button
                  key={getKey(opt)}
                  type="button"
                  onClick={() => {
                    onChange(opt)
                    setOpen(false)
                  }}
                  className={`w-full text-left px-3 py-2.5 hover:bg-primary/5 transition-colors border-b border-gray-100 last:border-b-0
                    ${isSelected ? 'bg-primary/10' : ''}
                  `}
                >
                  <p className={`text-sm font-medium truncate ${isSelected ? 'text-primary' : 'text-gray-800'}`}>
                    {getLabel(opt)}
                  </p>
                  {getSubLabel && (
                    <p className="text-[11px] text-gray-400 truncate mt-0.5">
                      {getSubLabel(opt)}
                    </p>
                  )}
                </button>
              )
            })}
            {isLoadingMore && (
              <div className="flex items-center justify-center gap-2 py-2 text-xs text-gray-400">
                <Icon icon="mdi:loading" className="animate-spin" />
                Loading more...
              </div>
            )}
          </div>
          <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-200 text-[11px] text-gray-400 text-right">
            {options.length} loaded{hasMore ? ' • scroll for more' : ''}
          </div>
        </div>
      )}
    </div>
  )
}

const WordPressConnectionsList = ({
  connections,
  selectedConnection,
  onSelectConnection,
  onGoToWebsiteConnections,
  platformName = 'WordPress',
  title = 'WordPress Connections',
  icon = 'mdi:wordpress',
  iconClassName = 'text-6xl text-[#21759b]',
  showPageSelector = true,
  showScanPlanner = false,
  pages = [],
  selectedPage = null,
  onSelectPage = () => { },
  isGettingPages = false,
  pagesError = null,
  onLoadMoreConnections,
  hasMoreConnections = false,
  isLoadingMoreConnections = false,
}) => {
  const platformLower = platformName.toLowerCase()

  if (connections.length === 0) {
    return (
      <Card title={title} className="mb-6">
        <div className="text-center py-8">
          <Icon icon={icon} className={`${iconClassName} mx-auto mb-4`} />
          <p className="text-text-secondary mb-4">No {platformName} connections found.</p>
          <Button variant="primary" onClick={onGoToWebsiteConnections} icon="mdi:plus">
            Add {platformName} Connection
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <Card title={title} className="mb-6">
      <div className={`grid grid-cols-1 ${showPageSelector ? 'md:grid-cols-2' : ''} gap-6`}>
        <div>
          <p className="text-sm text-text-secondary mb-4">
            Manage your {platformName} connections from the{' '}
            <button onClick={onGoToWebsiteConnections} className="text-primary hover:underline font-medium">
              Website Connections
            </button>{' '}
            page!
          </p>
          <div className="mb-0">
            <label className="form-label">Select {platformName} Connection</label>
            <CustomSelect
              options={connections}
              value={selectedConnection?._id || selectedConnection?.id || ''}
              onChange={onSelectConnection}
              placeholder="Select a connection..."
              getKey={(c) => c._id || c.id}
              getValue={(c) => c._id || c.id}
              getLabel={(c) => c.siteName}
              getSubLabel={(c) => c.siteUrl}
              onLoadMore={onLoadMoreConnections}
              hasMore={hasMoreConnections}
              isLoadingMore={isLoadingMoreConnections}
            />
            {selectedConnection?.siteUrl && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-gray-400 truncate flex-1">{selectedConnection.siteUrl}</span>
                <CopyButton text={selectedConnection.siteUrl} />
              </div>
            )}
          </div>
        </div>

        {showPageSelector && (
          <div>
            <p className="text-sm text-text-secondary mb-4">
              Select a page to run actions and audits for this {platformLower} connection.
            </p>
            <div className="mb-0">
              <label className="form-label flex items-center gap-2">
                Select {platformName} Page
                {isGettingPages && (
                  <Icon icon="mdi:loading" className="animate-spin text-primary" />
                )}
              </label>
              <CustomSelect
                options={pages}
                value={selectedPage?.id?.toString() || ''}
                onChange={onSelectPage}
                placeholder={
                  isGettingPages
                    ? 'Fetching pages...'
                    : pagesError
                      ? 'Error fetching pages'
                      : pages.length === 0
                        ? 'No pages found'
                        : 'Select a page...'
                }
                disabled={isGettingPages || connections.length === 0 || pages.length === 0}
                hasError={!!pagesError}
                getKey={(p) => p.id}
                getValue={(p) => p.id?.toString()}
                getLabel={(p) => p.title || 'Home Page'}
                getSubLabel={(p) => p.url}
              />
              {pagesError && (
                <p className="text-xs text-red-500 mt-1">{pagesError}</p>
              )}
              {selectedPage?.url && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-400 truncate flex-1">{selectedPage.url}</span>
                  <CopyButton text={selectedPage.url} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showPageSelector && showScanPlanner && (
        <ScanPlanner
          platformName={platformName}
          connections={connections}
          selectedConnection={selectedConnection}
          onSelectConnection={onSelectConnection}
          pages={pages}
          selectedPage={selectedPage}
          onSelectPage={onSelectPage}
          isGettingPages={isGettingPages}
          pagesError={pagesError}
        />
      )}
    </Card>
  )
}

export default WordPressConnectionsList
